// Settings > Account: study sync. The row is always there (search and #set= links land on it); its
// buttons show only when signed in and the server has sync on for this account, otherwise a line
// says why. The engine is the same lazy chunk the Re:Vault window runs (app/study-sync.js); both
// share revault:sync and take turns through a Web Lock.
import { getSession, authedFetch, accountConfig, onAccountChange } from "../../app/account.js";
import { readSync, statusLine, subHash, SYNC_KEY } from "../../shared/sync-state.js";
import { subscribe } from "../../shared/store.js";
import { inlineConfirm } from "../inline-confirm.js";

export const SYNC_HINT = "Cards, review progress, citations, exams and study days, plus your study totals and quest progress. Never your tabs, history or site logins.";
export const SYNC_OFF_NOTE = "Stopping sync on this device doesn't delete the copy in your account. Tick \"Also delete the copy in my account\" to remove it, or delete your account.";

let syncUrl = "";
try { syncUrl = __RVM.syncJs; } catch { /* unit tests have no build defines */ }
let engine = null;
// The sync engine, without the window's corner card (Settings shows its own buttons).
export function loadEngine() {
	if (!engine) engine = import(syncUrl).then((m) => m.browserSync({ getSession, authedFetch }, { ui: false })).catch((e) => { engine = null; throw e; });
	return engine;
}
// Is sync on for this account on this server?
export async function syncAvailable() {
	if (!getSession()) return false;
	try { const r = await authedFetch("/account/config"); const c = await r.json(); return !!(c && c.studySync); } catch { return false; }
}
// Signed out, the server only says yes when sync is on for everyone, so "sign in" is a real answer.
async function signInWouldSync() {
	if (getSession()) return false;
	try { return !!(await accountConfig()).studySync; } catch { return false; }
}
const me = () => { const s = getSession(); const sub = s && s.profile && s.profile.sub; return sub ? subHash(sub) : ""; };

function el(tag, cls, text) { const e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; }
function btn(label, onClick, cls) { const b = el("button", "rv-btn" + (cls ? " " + cls : ""), label); b.type = "button"; b.addEventListener("click", onClick); return b; }

export function studySyncBlock() {
	const box = el("div", "rv-sync");
	box.setAttribute("role", "group");
	box.setAttribute("aria-label", "Study sync");
	const status = el("p", "rv-account-note rv-sync-status");
	status.setAttribute("aria-live", "polite");
	const acts = el("div", "rv-account-actions");
	const offNote = el("p", "rv-account-note", SYNC_OFF_NOTE);
	// Why there's nothing to press, when sync isn't running for this account.
	const why = el("p", "rv-account-note rv-sync-why");
	const ctl = el("div", "rv-sync-controls");
	ctl.append(status, acts, offNote);
	box.append(el("p", "rv-account-note", SYNC_HINT), why, ctl);
	const show = (on, signIn) => {
		ctl.hidden = !on;
		why.hidden = on;
		why.textContent = on ? "" : signIn ? "Sign in to sync your study work." : "Study sync isn't on yet.";
	};
	show(false);
	let confirming = false;

	function draw() {
		const id = me();
		const st = readSync();
		status.textContent = statusLine(st, id) || "Getting ready to sync.";
		if (confirming) return;
		acts.textContent = "";
		offNote.hidden = st.ask === id || st.off === id;
		const act = (label, fn, cls) => acts.append(btn(label, async (e) => {
			e.currentTarget.disabled = true;
			try { await fn(await loadEngine()); } catch { status.textContent = "That didn't work. Try again."; }
			draw();
		}, cls));
		if (st.ask === id) {
			act("Add them", (s) => s.accept(), "rv-btn-primary");
			act("Not now", (s) => s.decline());
			return;
		}
		if (st.off === id) { act("Turn on", (s) => s.turnOn(), "rv-btn-primary"); return; }
		if (st.paused) act("Try again", (s) => s.retry());
		else act("Sync now", (s) => s.retry());
		const stop = btn("Stop syncing on this device", () => {
			confirming = true;
			const c = inlineConfirm({
				opener: stop,
				text: "Stop syncing study work on this device? It stays here, and the copy in your account stays too unless you tick the box.",
				okLabel: "Stop syncing",
				onOk: async (go, close) => {
					go.disabled = true;
					let ok = true;
					try { ok = await (await loadEngine()).turnOff(cb.checked); } catch { ok = false; }
					confirming = false;
					close();
					draw();
					if (cb.checked) status.textContent = ok ? "Sync is off on this device, and the copy in your account is deleted." : "Sync is off on this device, but the copy in your account couldn't be deleted. Try again.";
				},
				onCancel: () => { confirming = false; },
			});
			const lab = el("label", "rv-way-check");
			const cb = el("input"); cb.type = "checkbox"; cb.checked = false;
			lab.append(cb, document.createTextNode(" Also delete the copy in my account"));
			c.box.insertBefore(lab, c.box.lastChild);
			stop.hidden = true;
			acts.after(c.box);
			c.go.focus();
		}, "rv-btn-danger");
		acts.append(stop);
	}

	let gen = 0;
	async function refresh() {
		const my = ++gen;
		const on = await syncAvailable();
		const signIn = !on && await signInWouldSync();
		if (my !== gen) return;
		show(on, signIn);
		if (on) { draw(); loadEngine().then((s) => { if (!readSync().sub && !readSync().ask && !readSync().off) s.begin().catch(() => {}); }).catch(() => {}); }
	}
	subscribe(SYNC_KEY, () => { if (!ctl.hidden) draw(); });
	onAccountChange(() => refresh());
	setTimeout(refresh, 0);
	return box;
}

// For the sign-out confirm: whether this device's study work is all in the account, so taking it
// off this device loses nothing. null when sync isn't running here.
export async function signOutCheck() {
	const st = readSync();
	if (!st.sub || st.sub !== me() || st.off === st.sub) return null;
	try {
		const s = await loadEngine();
		// Something waiting: try to send it first, so taking the work off this device loses nothing.
		if (!s.synced()) await s.push().catch(() => {});
		return { synced: s.synced() };
	} catch { return { synced: false }; }
}
